import type { PageResponse } from '@mall/shared';
import { apiClient } from '../../app/api';
import type {
  BrandView,
  CreateProductSyncRequest,
  ListProductsParams,
  ProductSnapshot,
  ProductSyncJobView,
} from './types';

export function listProducts(params: ListProductsParams): Promise<PageResponse<ProductSnapshot>> {
  return apiClient.get<PageResponse<ProductSnapshot>>('/operator/products', {
    query: {
      tenant_id: params.tenantId,
      page: params.page ?? 1,
      page_size: params.pageSize ?? 20,
      keyword: params.keyword || undefined,
      category_id: params.categoryId ?? undefined,
      brand_id: params.brandId ?? undefined,
      status: params.status ?? undefined,
      price_min_cents: params.priceMinCents ?? undefined,
      price_max_cents: params.priceMaxCents ?? undefined,
      original_price_min_cents: params.originalPriceMinCents ?? undefined,
      original_price_max_cents: params.originalPriceMaxCents ?? undefined,
    },
  });
}

export function getProduct(tenantId: number, id: number): Promise<ProductSnapshot> {
  return apiClient.get<ProductSnapshot>(`/operator/products/${id}`, {
    query: { tenant_id: tenantId },
  });
}

export function updateProductPrice(
  tenantId: number,
  id: number,
  priceCents: number,
): Promise<{ updated: number }> {
  return apiClient.patch<{ updated: number }>(
    `/operator/products/${id}/price`,
    { price_cents: priceCents },
    { query: { tenant_id: tenantId } },
  );
}

export interface UpdateProductImagesRequest {
  mainImage: string;
  galleryImageUrls: string[];
}

export function updateProductImages(
  tenantId: number,
  id: number,
  payload: UpdateProductImagesRequest,
): Promise<ProductSnapshot> {
  return apiClient.patch<ProductSnapshot>(
    `/operator/products/${id}/images`,
    {
      main_image: payload.mainImage,
      images: payload.galleryImageUrls,
    },
    { query: { tenant_id: tenantId } },
  );
}

export function batchUpdateProductPrice(
  tenantId: number,
  ids: number[],
  priceCents: number,
): Promise<{ updated: number }> {
  return apiClient.patch<{ updated: number }>(
    '/operator/products/batch-price',
    { ids, price_cents: priceCents },
    { query: { tenant_id: tenantId } },
  );
}

export function batchGenerateProductPrice(
  tenantId: number,
  ids: number[],
  coefficient: number,
): Promise<{ updated: number }> {
  return apiClient.patch<{ updated: number }>(
    '/operator/products/batch-generate-price',
    { ids, coefficient },
    { query: { tenant_id: tenantId } },
  );
}

export function unpublishProduct(tenantId: number, id: number): Promise<void> {
  return apiClient.post<void>(`/operator/products/${id}/unpublish`, undefined, {
    query: { tenant_id: tenantId },
  });
}

export function batchUnpublishProducts(
  tenantId: number,
  ids: number[],
): Promise<{ updated: number }> {
  return apiClient.post<{ updated: number }>(
    '/operator/products/batch-unpublish',
    { ids },
    { query: { tenant_id: tenantId } },
  );
}

export function batchPublishProducts(
  tenantId: number,
  ids: number[],
): Promise<{ updated: number }> {
  return apiClient.post<{ updated: number }>(
    '/operator/products/batch-publish',
    { ids },
    { query: { tenant_id: tenantId } },
  );
}

export function batchDeleteProducts(
  tenantId: number,
  ids: number[],
): Promise<{ updated: number }> {
  return apiClient.post<{ updated: number }>(
    '/operator/products/batch-delete',
    { ids },
    { query: { tenant_id: tenantId } },
  );
}

export function listBrands(tenantId: number): Promise<BrandView[]> {
  return apiClient.get<BrandView[]>('/operator/brands', {
    query: { tenant_id: tenantId },
  });
}

export function createProductSyncJob(
  payload: CreateProductSyncRequest,
): Promise<ProductSyncJobView> {
  return apiClient.post<ProductSyncJobView>('/operator/products/sync', {
    source_tenant_id: payload.sourceTenantId,
    target_tenant_id: payload.targetTenantId,
    mode: payload.mode,
    category_id: payload.categoryId ?? undefined,
    product_ids: payload.productIds ?? undefined,
  });
}

export function getProductSyncJob(jobId: number): Promise<ProductSyncJobView> {
  return apiClient.get<ProductSyncJobView>(`/operator/products/sync/${jobId}`);
}

export function setProductSyncLock(
  tenantId: number,
  id: number,
  locked: boolean,
): Promise<void> {
  return apiClient.patch<void>(
    `/operator/products/${id}/sync-lock`,
    { locked },
    { query: { tenant_id: tenantId } },
  );
}
